import { z } from 'zod';
import { emailSchema, phoneSchema } from '../../validation';
import {
  customerGroupTypesSchema,
  idEntrySchema,
  partyRoleSchema,
  partySchema,
  partyTargetSchema,
} from '../../common';
import { nameSchema, objectIdSchema } from '../../validation';
import { refSchema } from '../../common';

const customerTypeSchema = customerGroupTypesSchema;

//* typed
export const orderBeneficiaryTypeSchema = z.enum(['receiver', 'sender', 'shipper', 'not_sure']);
/**
 * - receiver = the customer the items will be shipped to (dropoff side)
 * - sender   = the party we pick the order up from (pickup side)
 * - shipper  = the actual app shipper ({@link OrderSchema}.shipper) — the publisher of the order
 *
 * Phase 7 rename — `'user'` was renamed to `'shipper'` so the contact
 * `sameAs` axis matches the network-neutral name on `Order.shipper`.
 * Old documents that wrote `sameAs: 'user'` will fail Zod parse on
 * read; this is intentional (no migration).
 */
export type OrderBeneficiaryType = z.infer<typeof orderBeneficiaryTypeSchema>;

//* contact
export const orderContactSchema = z.object({
  /**  this will come from contact collection if the user want to save it
   *
   * if empty, then the user did not save it to his contact and does't want to use it again ... it's one time thing
   */
  _id: objectIdSchema.optional(),

  /** name of the contact person ... this is NOT necessarily the same as customername.
   *
   * For example, when the type of customer is `company`, then customer.name will be "Apple",
   * and the contact.name will be "Abdul".
   *
   * However, if the customer is individual, then both customer.name & contact.name will be "Abdul" .
   */
  name: nameSchema,

  /** the phone number of the contact person */
  phone: phoneSchema,

  /** the email of the contact person */
  email: emailSchema.optional(),

  /**
   * same as contact ... if `shipper`, then the contact info should be
   * the one on `Order.shipper.contact`.
   */
  sameAs: orderBeneficiaryTypeSchema,
});
export type OrderContact = z.infer<typeof orderContactSchema>;

//* beneficiary
export const orderBeneficiarySchema = z.object({
  /**
   * the _id of the full doc ...
   *
   * If empty, then the info was not saved in any collection for reuse again in the future.
   */
  _id: objectIdSchema.optional(),

  /** a custom ref by the user */
  ref: refSchema.optional(),
  /** the type of customer such as company | individual */
  customerType: customerTypeSchema,

  /** name of the beneficiary */
  name: nameSchema,
  /** the Beneficiary contact person ... this is NOT necessarily the same as Beneficiary.name
   *
   * For example, when the type of customer is `company`, then customer.name will be "Apple",
   * and the contact.name will be "Tim Cook".
   *
   * However, if the customer is individual, then both Beneficiary.name & Beneficiary.name will be "Abdul".
   *
   * Optional: at create-time we sometimes know the beneficiary (a CRM
   * Client picked from the dropdown) without a callable phone yet —
   * the Client doc may only carry a name + address. In that case the
   * receiver is stamped with `target` / `roles[]` / `ids[]` for tenant
   * visibility but `contact` is omitted entirely rather than written
   * with an empty-string phone (which fails {@link phoneSchema}).
   */
  contact: orderContactSchema.optional(),
  /** the info of the beneficiary is the same as `shipper`, `sender`, `receiver`, etc. */
  sameAs: orderBeneficiaryTypeSchema,

  // ──────────── Phase 1 — additive Party shape ────────────
  //
  // `Order.receiver` is the party-side counterparty when the originator
  // creates an order for a CRM Client (the publish-order flow on the
  // carrier dashboard). Before Phase 1 it carried only the legacy
  // beneficiary fields above (`_id`/`name`/`customerType`/`contact`),
  // which left `TenantsBuilder` blind to the buyer's identifier-equivalents
  // when `Order.contracts[]` was still empty (auto-allocation, broadcast,
  // walk-in). Adding `target` / `roles[]` / `ids[]` here lets the buyer
  // see the Order in their inbox from minute zero — without forcing a
  // contract seed.
  //
  // Fields are OPTIONAL and additive: legacy docs (and walk-in flows
  // where no CRM Client was picked) keep validating as-is. Migration
  // `relations-v2-order-contracts-and-timeline` (Phase 7) backfills
  // these fields on existing docs whose `_id` resolves to an Org / Client.

  /** which collection the receiver's primary `_id` belongs to. */
  target: partyTargetSchema.optional(),
  /** the receiver's network roles. Multi-valued; `roles[0]` is the convention for primary-role display. */
  roles: z.array(partyRoleSchema).optional(),
  /**
   * identifier-equivalents (mirrors {@link partySchema.ids}). When set,
   * the first entry is the primary `_id` paired with the matching
   * {@link idTypeSchema} value. `TenantsBuilder.recompute` walks this
   * array directly, so a Party-shaped receiver emits buyer tenants
   * (`client_id` / `profile_id` / `org_id`) even when `contracts: []`.
   */
  ids: z.array(idEntrySchema).optional(),
});

/**
 * The `Order.shipper` snapshot — a Party-shaped reference to whoever
 * **published** the order. Network-neutral by design: the shipper can
 * be a business customer publishing for themselves, a carrier
 * publishing on behalf of a client, a broker publishing for resale,
 * or a customer-org publishing for an internal move. It is NOT
 * equivalent to "the buyer of the lane" — direction lives on
 * `Order.contracts[i].buyer/seller`, not here.
 *
 * Re-implemented in terms of {@link partySchema} (was previously
 * `relationSchema.extend`). The Party snapshot carries the publisher's
 * `roles[]` and `ids[]` so downstream code can render multi-role
 * labels without extra lookups. The `contact` extension is preserved
 * because `tenants.builder.ts` walks `shipper.contact.{phone,email,_id}`
 * for late-binding tenant matching (a person signs up later with the
 * same phone and inherits their pre-signup orders).
 *
 * See `docs/relations-v2/02-data-model.md` section 12 and
 * `apps/backend/src/app/order/README.md` for the full mental model.
 */
export const orderShipperSchema = partySchema.extend({
  contact: orderContactSchema.optional(),
});
/** the party who published the order — see {@link orderShipperSchema} */
export type OrderShipper = z.infer<typeof orderShipperSchema>;

/** a beneficiary is a receiver / sender / shipper-side party on the order */
export type OrderBeneficiary = z.infer<typeof orderBeneficiarySchema>;

//* Destination
export const orderBeneficiaryDestinationSchema = orderBeneficiarySchema.omit({
  contact: true,
});

export type OrderBeneficiaryDestination = z.infer<typeof orderBeneficiaryDestinationSchema>;
